import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import EmptyData from '../../../../common/emptyData/EmptyData';
import useFieldLabelsAPI from '../../../../../hooks/useFieldLabelsAPI';
import { buildAnnotationMap, buildFormattingOptions, formatByType } from '../../../../../helpers/fieldValueFormatter';
import css from './styles.css';

function GenericNumericalView({
  resultSet,
  showLabel = true,
  customLabel,
  reportType,
  formatting,
  fieldAliasMap = {},
}) {
  const { labelsMap: dimLabels } = useFieldLabelsAPI({ fieldType: 'dimension' });
  const { labelsMap: measLabels } = useFieldLabelsAPI({ fieldType: 'measure' });
  const labelsMap = { ...dimLabels, ...measLabels };

  const annotationMap = useMemo(() => buildAnnotationMap(resultSet), [resultSet]);
  const fmtOptions = useMemo(() => buildFormattingOptions(formatting, null), [formatting]);

  const resolveLabel = (columnKey) => {
    // columnKey may be "cataloging_view.total_instances" (dot) or
    // "cataloging_view_total_instances" (underscore from table view)
    // Try dot-notation first (direct from resultSet)
    const alias = fieldAliasMap?.[columnKey];
    if (alias && labelsMap?.[alias]) return labelsMap[alias];
    if (labelsMap?.[columnKey]) return labelsMap[columnKey];

    // Try converting underscores back to dot-notation
    const parts = columnKey.split('_');
    for (let i = 1; i < parts.length; i++) {
      const candidate = parts.slice(0, i).join('_') + '.' + parts.slice(i).join('_');
      const candidateAlias = fieldAliasMap?.[candidate];
      if (candidateAlias && labelsMap?.[candidateAlias]) return labelsMap[candidateAlias];
      if (labelsMap?.[candidate]) return labelsMap[candidate];
    }

    // Fallback: humanize last segment
    const lastSegment = columnKey.includes('.')
      ? columnKey.split('.').pop()
      : parts[parts.length - 1];
    return lastSegment.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (!resultSet || !resultSet.loadResponses?.length) {
    return <EmptyData />;
  }

  const data = resultSet.loadResponses?.[0]?.data?.[0];
  if (!data) {
    return <EmptyData />;
  }

  // Get all numeric columns
  const numericColumns = Object.entries(data).filter(
    ([_, value]) => typeof value === 'number' ||
      (typeof value === 'string' && !Number.isNaN(Number(value))),
  );

  if (numericColumns.length === 0) {
    return <EmptyData />;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
      {numericColumns.map(([columnName, value]) => {
        const columnKey = columnName.replace('.', '_');
        const fieldEntry = annotationMap[columnKey];
        const fieldType = fieldEntry?.type || fieldEntry || null;
        const formattedValue = formatByType(value, fieldType, fmtOptions);
        const translatedLabel = resolveLabel(columnName);
        const label = customLabel || translatedLabel;
        return (
          <div key={columnName} className={css.card}>
            {showLabel && <div className={css.cardHeader}>{label}</div>}
            <div className={css.cardBody}>{formattedValue}</div>
          </div>
        );
      })}
    </div>
  );
}

GenericNumericalView.propTypes = {
  resultSet:     PropTypes.object.isRequired,
  showLabel:     PropTypes.bool,
  customLabel:   PropTypes.string,
  reportType:    PropTypes.string,
  formatting:    PropTypes.object,
  fieldAliasMap: PropTypes.object,
};

export default GenericNumericalView;
